import { useTranslation } from 'react-i18next';
import { createContext, useState, useCallback, useMemo, useEffect } from 'react';
import { Routes } from '@/routes';
import { parseError } from '@/utils';
import { SelfHttp } from '@/services';
import { useDevice, useRefresh } from '@/hooks';
import { useUserContext } from '../UserContext';
import { useAppContext } from '../AppContext';
import { getSelfTypeByVaraiant, getVaraiantBySelfType } from './util';

export type SelfContextType = {
  items: SelfEntity[];
  currentSelfItems: SelfEntity[];
  refresh: () => void;
  loading: boolean;
  error: string;
  findTypeById: (id: number) => SelfType;
  findVariantById: (id: number) => ComponentVariant;
  currentSelf: SelfEntity;
  showingDate: string;
  changeWeek: (value: string) => void;
  selectSelf: (self: SelfEntity) => void;
  selfText: (key: string) => string;
  redirectToSelf: ({ mode, navigate }) => any;
  redirectToReserve: ({ mode, navigate }) => any;
  checkMobileSelfPage: ({ mode, navigate }) => any;
}

export const SelfContext = createContext<SelfContextType>(null);

const getSelfUrlPath = (variant: ComponentVariant) => ({
  'nurture': Routes.FoodSelfs,
  'sport': Routes.SportGyms,
  'health': Routes.HealthClinics,
  'transport': Routes.TransportTerminals,
}[String(variant)]);

const getReservePath = (variant: ComponentVariant) => ({
  'nurture': Routes.FoodReserve,
  'sport': Routes.SportReserve,
  'health': Routes.HealthReserve,
  'transport': Routes.TransportReserve,
}[String(variant)]);


export const SelfContextProvider = ({ children }) => {
  const [t] = useTranslation(['common', 'reserve']);

  const { isDesktop } = useDevice();
  const { isAuth } = useUserContext();
  const { mode } = useAppContext();

  const [showingDate, setShowingDate] = useState('');
  const [currentSelf, setCurrentSelf] = useState<SelfEntity>(null);
  const [items, setItems] = useState<SelfEntity[]>([]);
  const [error, setError] = useState<string>(null);
  const [loading, setLoading] = useState(false);
  const [refreshCounter, refresh] = useRefresh();

  // Fetch all selfs (includes sports, nurture)
  useEffect(() => {
    if (!isAuth) return;
    setLoading(true);
    setError(null);
    SelfHttp.fetch()
      .then((response) => {
        const data = (response?.data?.payload ?? []).map(x => {
          if (x.type == null || x.type == '') {
            x.type = 'NORMAL';
          }
          return x;
        });
        setItems(data);
      })
      .catch((e) => setError(parseError(e)))
      .finally(() => setLoading(false));
  }, [refreshCounter, isAuth]);

  useEffect(() => {
    setCurrentSelf(null);
  }, [mode]);

  const findTypeById = useCallback((id: number) => {
    const found = items.find(x => Number(x.id) === Number(id));
    return found?.type;
  }, [items]);

  const findVariantById = useCallback((id: number) => {
    return getVaraiantBySelfType(findTypeById(id));
  }, [findTypeById]);

  const selfText = useCallback((key: string) => {
    return t(`reserve:context.${key}.${mode}`);
  }, [mode, t]);

  const redirectToReserve = useCallback(({ mode, navigate }) => {
    navigate(getReservePath(mode), { replace: true });
  }, []);

  const redirectToSelf = useCallback(({ mode, navigate }) => {
    navigate(getSelfUrlPath(mode), { replace: true });
  }, []);

  const checkMobileSelfPage = useCallback(({ mode, navigate }) => {
    if (!currentSelf && !isDesktop()) {
      redirectToSelf({ mode, navigate });
    }
  }, [redirectToSelf, currentSelf]);

  const selectSelf = useCallback((self: SelfEntity) => {
    setCurrentSelf(self);
  }, [setCurrentSelf]);

  const currentSelfItems = useMemo(() => {
    const type = getSelfTypeByVaraiant(mode);
    return items.filter(x => x.type === type);
  }, [items, currentSelf, mode]);

  return (
    <SelfContext.Provider value={{
      currentSelf,
      selectSelf,
      items,
      findTypeById,
      findVariantById,
      refresh,
      loading,
      error,
      selfText,
      currentSelfItems,
      checkMobileSelfPage,
      redirectToReserve,
      redirectToSelf,
      showingDate,
      changeWeek: setShowingDate
    }}>
      {children}
    </SelfContext.Provider>
  );
};
